import cn from 'classnames';
import { useRef } from 'react';
import { useRect } from '#hooks/useRect.ts';
import { useScrollbarWidth } from '#hooks/useScrollbarWidth.ts';
import styles from './ConfirmChangesBanner.module.css';

type Props = {
  title?: React.ReactNode;
  isOpen?: boolean;
  /** The primary action in the {@link ConfirmChangesBanner}, often `Save` or `Confirm` */
  primaryButton: React.ReactNode;
  /** The secondary action in the {@link ConfirmChangesBanner}, often `Cancel` or `Clear` */
  secondaryButton?: React.ReactNode;
};

/**
 * {@link ConfirmChangesBanner} provides a floating banner to signal a dirty full-page form.
 * see http://localhost:6006/?path=/story/patterns-modals-confirmchangesbanner--lorem-ipsum-no-overlap
 */
export function ConfirmChangesBanner({
  title = 'You have unsaved changes',
  isOpen = true,
  primaryButton,
  secondaryButton,
}: Props) {
  const bannerRef = useRef<HTMLDivElement>(null);
  const { height: bannerHeight } = useRect(bannerRef, { observe: isOpen }) ?? {};
  const scrollbarWidth = useScrollbarWidth('#page-main');

  if (!isOpen) return null;

  return (
    /** Spacer for adding the required padding */
    <div
      className={styles.ConfirmChangesBanner_spacer}
      style={{
        '--banner-height': bannerHeight ? `${bannerHeight}px` : undefined,
        '--scrollbar-width': scrollbarWidth ? `${scrollbarWidth}px` : undefined,
      }}
    >
      <div ref={bannerRef} className={styles.ConfirmChangesBanner} aria-live="polite">
        <p className={cn('p-md', styles.ConfirmChangesBanner_title)}>{title}</p>
        <div className={styles.ConfirmChangesBanner_confirmButtons}>
          {secondaryButton}
          {primaryButton}
        </div>
      </div>
    </div>
  );
}
